import { Link } from 'react-router-dom';
import { User, Key, ShoppingCart, Calendar } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useLicenses } from '../hooks/useLicenses';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import { getLicenseState, hasAvailableActivations, LicenseState } from '../utils/license-utils';

const LICENSE_STATE_BADGES: Record<LicenseState, { label: string; className: string }> = {
  active: {
    label: 'Active',
    className: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400',
  },
  expired: {
    label: 'Expirée',
    className: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400',
  },
  inactive: {
    label: 'Désactivée',
    className: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400',
  },
};

const LICENSE_STATE_ORDER: Record<LicenseState, number> = { active: 0, expired: 1, inactive: 2 };

export default function DashboardPage() {
  const { userProfile } = useAuth();
  const { licenses, transactions, getActiveLicenses } = useLicenses();

  const activeLicenses = getActiveLicenses();
  // Toutes les licences de l'utilisateur, les actives d'abord (tri stable : récentes en tête)
  const sortedLicenses = [...licenses].sort(
    (a, b) => LICENSE_STATE_ORDER[getLicenseState(a)] - LICENSE_STATE_ORDER[getLicenseState(b)]
  );
  const totalSpent = transactions.reduce((sum, transaction) =>
    transaction.status === 'completed' ? sum + transaction.amount : sum, 0
  );

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* En-tête de bienvenue */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Bienvenue, {userProfile?.full_name || 'Utilisateur'} !
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Gérez vos licences et accédez à vos téléchargements
            </p>
          </div>

          {/* Statistiques rapides */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Licences actives</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {activeLicenses.length}
                  </p>
                </div>
                <Key className="h-12 w-12 text-purple-600" />
              </div>
            </div>

            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total dépensé</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {formatPrice(totalSpent)}
                  </p>
                </div>
                <ShoppingCart className="h-12 w-12 text-green-600" />
              </div>
            </div>

            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Transactions</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {transactions.length}
                  </p>
                </div>
                <Calendar className="h-12 w-12 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-3xl mx-auto">
            <Link
              to="/profile"
              className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 hover:bg-white/20 dark:hover:bg-gray-900/20 transition-all duration-200 group"
            >
              <div className="text-center">
                <User className="h-12 w-12 text-purple-600 mx-auto mb-4 group-hover:scale-110 transition-transform duration-200" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Mon Profil
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Gérer mes informations
                </p>
              </div>
            </Link>

            <Link
              to="/purchase"
              className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 hover:bg-white/20 dark:hover:bg-gray-900/20 transition-all duration-200 group"
            >
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-green-600 mx-auto mb-4 group-hover:scale-110 transition-transform duration-200" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Acheter
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Nouvelle licence
                </p>
              </div>
            </Link>

          </div>

          {/* Licences récentes */}
          {sortedLicenses.length > 0 && (
            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Mes licences
                </h2>
              </div>

              <div className={`grid gap-6 ${
                sortedLicenses.length === 1
                  ? 'grid-cols-1 max-w-2xl mx-auto'
                  : 'grid-cols-1 lg:grid-cols-2'
              }`}>
                {sortedLicenses.map((license) => {
                  const state = getLicenseState(license);
                  const badge = LICENSE_STATE_BADGES[state];
                  const allSlotsUsed = !hasAvailableActivations(license);

                  return (
                  <div
                    key={license.id}
                    className="bg-white/10 dark:bg-gray-900/10 rounded-xl p-6 border border-white/10 hover:border-white/30 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {license.license_types?.name}
                      </h3>
                      <span className={`${badge.className} px-3 py-1 rounded-full text-sm font-medium`}>
                        {badge.label}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      {license.license_types?.description}
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Numéro de licence :</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                          {license.license_key}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Date d'achat :</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {new Date(license.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>

                      {license.purchase_reference && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Référence paiement Stripe :</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            {license.purchase_reference}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Appareils autorisés :</span>
                        <span className={`text-sm ${
                          allSlotsUsed
                            ? 'text-orange-600 dark:text-orange-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {license.current_activations || 0}/{license.max_activations}
                          {allSlotsUsed && ' · Tous vos postes sont utilisés'}
                        </span>
                      </div>

                      {license.expires_at && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Expire le :</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {new Date(license.expires_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Message si aucune licence */}
          {sortedLicenses.length === 0 && (
            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-12 text-center">
              <Key className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Aucune licence
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Achetez une licence pour commencer à utiliser MP3 Random Selector avec toutes ses fonctionnalités.
              </p>
            </div>
          )}

          {/* Licences présentes mais aucune active (expirées ou désactivées) */}
          {sortedLicenses.length > 0 && activeLicenses.length === 0 && (
            <p className="mt-6 text-center text-gray-600 dark:text-gray-300">
              Aucune de vos licences n'est active. <Link to="/purchase" className="underline text-purple-400">Acheter ou renouveler une licence</Link>.
            </p>
          )}
        </div>
      </div>
    </ProtectedRoute>
  );
}
